import { GameStatus } from '../../core/constants.js';
import gameState from '../../core/game-state.js';
import { clearAllScores } from '../../core/highscore.js';
import { getPausedFromStatus, resetToTitle } from '../../gameplay/game-controls.js';
import { getAllSkillTypes, getSkillDefinition } from '../../gameplay/skills/definitions.js';
import { STATE_PATHS } from '../../shared/constants/state-paths.js';
import { MinimalErrorTracker } from '../../shared/log.js';
import { UI_CONSTANTS, DOM_CONSTANTS } from '../constants.js';
import { createSkillIcon } from '../helpers/icon-renderer.js';
import { guardAsyncButton } from '../input/button-guard.js';
import { resetTutorialProgress } from '../tutorial/tutorial-reset.js';

const CSS_CLASS_SETTINGS_GROUP = 'settings-group';
const SKILL_CARD_CLASS = 'skill-card';
const SKILL_CARD_ICON_CLASS = 'skill-card__icon';
const SKILL_CARD_NAME_CLASS = 'skill-card__name';
const SKILL_CARD_DESC_CLASS = 'skill-card__desc';
const SETTINGS_ACTION_LIST_CLASS = 'settings-action-list';
const SETTINGS_ACTION_CARD_CLASS = 'settings-action-card';
const SETTINGS_ACTION_CARD_NAME_CLASS = 'settings-action-card__name';

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const SVG_VIEWBOX_DEFAULT = '0 -960 960 960';
const RESET_GAME_MESSAGE = '変更を適用するためゲームを再開始します。\n続けますか?';

function getEffectiveCoreStatus() {
  const pausedSourceStatus = getPausedFromStatus();
  if (pausedSourceStatus) {
    return pausedSourceStatus;
  }
  return gameState.getState(STATE_PATHS.CORE_STATUS);
}

function closeSettingsView(closeSettings, menuSystem, { force = false } = {}) {
  if (!force) {
    return;
  }

  if (typeof closeSettings === 'function') {
    closeSettings();
    return;
  }

  if (menuSystem && typeof menuSystem.closeSettings === 'function') {
    menuSystem.closeSettings();
  }
}

async function handleCriticalSettingChange(
  action,
  closeSettings,
  message = RESET_GAME_MESSAGE,
  menuSystem = null,
  { closeOnComplete = true } = {},
) {
  const originalStatus = getEffectiveCoreStatus();

  // Title状態からMenuを開いた場合は即座に変更OK
  if (originalStatus === GameStatus.Title) {
    action();
    closeSettingsView(closeSettings, menuSystem, { force: closeOnComplete });
    return true;
  }

  // Playing/Ready状態からMenuを開いた場合は確認が必要
  let confirmed = false;
  if (menuSystem && typeof menuSystem.showConfirmDialog === 'function') {
    confirmed = await menuSystem.showConfirmDialog({
      message,
      confirmLabel: 'Restart',
      restoreOnConfirm: false,
    });
  } else {
    confirmed = confirm(message);
  }

  if (confirmed) {
    try {
      await resetToTitle();
      action();
      const shouldForceClose = closeOnComplete || originalStatus !== GameStatus.Title;
      closeSettingsView(closeSettings, menuSystem, { force: shouldForceClose });
      return true;
    } catch (error) {
      MinimalErrorTracker.warn('UI', 'Critical setting change failed', error);
      return false;
    }
  }

  return false;
}

const SettingsHelper = {
  getSetting(key, fallback) {
    const settings = gameState.getState(STATE_PATHS.SETTINGS);
    return settings[key] !== undefined ? settings[key] : fallback;
  },

  setSetting(key, value) {
    gameState.setState(`settings.${key}`, value);
  },
};

function createSVGIcon(pathData, className = '', viewBox = SVG_VIEWBOX_DEFAULT) {
  const svg = document.createElementNS(SVG_NAMESPACE, 'svg');
  svg.setAttribute('class', className);
  svg.setAttribute('xmlns', SVG_NAMESPACE);
  svg.setAttribute('viewBox', viewBox);
  svg.setAttribute('fill', 'currentColor');

  const path = document.createElementNS(SVG_NAMESPACE, 'path');
  path.setAttribute('d', pathData);
  path.setAttribute('fill', 'currentColor');

  svg.appendChild(path);
  return svg;
}

function createActionCard(labelText) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = `btn-base ${SETTINGS_ACTION_CARD_CLASS}`;
  button.setAttribute('aria-label', labelText);

  const name = document.createElement('span');
  name.className = SETTINGS_ACTION_CARD_NAME_CLASS;
  name.textContent = labelText;

  button.appendChild(name);
  return button;
}

async function showConfirmation({ message, confirmLabel = 'OK', menuSystem = null }) {
  if (menuSystem && typeof menuSystem.showConfirmDialog === 'function') {
    return await menuSystem.showConfirmDialog({
      message,
      confirmLabel,
      restoreOnConfirm: false,
    });
  }
  return confirm(message);
}

const DOMHelper = {
  getSettingsRefs() {
    const drawer = document.getElementById('settings-drawer');
    const content = drawer ? drawer.querySelector('.settings-content') : null;
    const actions = content ? content.querySelector('.settings-actions') : null;
    const titleEl = content ? content.querySelector('.settings-title') : null;
    const backBtn = content ? content.querySelector('#settings-back') : null;
    return { drawer, content, actions, titleEl, backBtn };
  },

  initializeSettingsView(className = 'settings-view u-transform-center-x') {
    const { content, actions } = this.getSettingsRefs();
    if (!content) return null;

    if (actions) actions.style.display = 'none';
    this.clearSettingsView();

    const view = document.createElement('div');
    view.className = className;

    return { view, content };
  },

  clearSettingsView() {
    const { content } = this.getSettingsRefs();
    if (!content) return;

    const prev = content.querySelector('.settings-view');
    if (prev) prev.remove();
  },

  setActiveWithin(container, selector, activeElement) {
    // 同じグループ内の全要素からis-activeクラスを削除
    container.querySelectorAll(selector).forEach((el) => {
      el.classList.remove(DOM_CONSTANTS.ACTIVE_CLASS);
    });
    // 指定要素にis-activeクラスを追加
    activeElement.classList.add(DOM_CONSTANTS.ACTIVE_CLASS);
  },

  createSettingsGroup(labelText, inputElement) {
    const group = document.createElement('div');
    group.className = CSS_CLASS_SETTINGS_GROUP;

    const label = document.createElement('div');
    label.className = 'settings-label';
    label.textContent = labelText;

    group.appendChild(label);
    group.appendChild(inputElement);

    return group;
  },
};

const RecordsRenderer = {
  render(_ctx) {
    const result = DOMHelper.initializeSettingsView();
    if (!result) return;
    const { view, content } = result;

    // 統計データを取得
    const statsData = gameState.getState(STATE_PATHS.STATS);
    const playCount = statsData.playCount || 0;
    const bestScore = statsData.bestScore || 0;
    const longestChain = statsData.longestChain || 0;
    const bestMatch = statsData.bestMatch || 0;

    // Recordsテーブル
    const table = document.createElement('div');
    table.className = 'records-table';

    // 各統計項目を作成
    const records = [
      { label: 'Plays', value: playCount.toLocaleString() },
      { label: 'Best Score', value: bestScore.toLocaleString() },
      { label: 'Max Combo', value: longestChain.toLocaleString() },
      { label: 'Max Clear', value: bestMatch.toLocaleString() },
    ];

    records.forEach((record) => {
      const row = document.createElement('div');
      row.className = 'records-row';

      const label = document.createElement('span');
      label.className = 'records-label';
      label.textContent = record.label;

      const value = document.createElement('span');
      value.className = 'records-value';
      value.textContent = record.value;

      row.appendChild(label);
      row.appendChild(value);
      table.appendChild(row);
    });

    view.appendChild(table);
    content.appendChild(view);
  },
};

const StyleRenderer = {
  createModeCard(mode, isActive) {
    const icon = createSVGIcon(mode.iconPath, 'settings-toggle__icon');

    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'settings-toggle__btn';
    if (isActive) {
      card.classList.add('is-active');
    }

    const name = document.createElement('div');
    name.className = 'settings-toggle__name';
    name.textContent = mode.name;

    const desc = document.createElement('div');
    desc.className = 'settings-toggle__desc';
    desc.textContent = mode.desc;

    card.appendChild(icon);
    card.appendChild(name);
    card.appendChild(desc);

    return card;
  },

  attachModeHandler(card, mode, grid, ctx) {
    card.addEventListener(
      'click',
      guardAsyncButton(async () => {
        const currentModeNow = SettingsHelper.getSetting('gameMode', 'Blitz');
        if (currentModeNow === mode.id) {
          return;
        }

        await handleCriticalSettingChange(
          () => {
            SettingsHelper.setSetting('gameMode', mode.id);
            const gameModeValue = document.getElementById('game-mode-value');
            if (gameModeValue) gameModeValue.textContent = mode.id;

            // 選択状態を更新
            grid
              .querySelectorAll('.settings-toggle__btn')
              .forEach((c) => c.classList.remove('is-active'));
            card.classList.add('is-active');
          },
          ctx?.closeSettings,
          '今のプレイがリセットされます。\nモードを変更しますか?',
          ctx?.menuSystem,
          { closeOnComplete: false },
        );
      }),
    );
  },

  render(ctx) {
    const result = DOMHelper.initializeSettingsView();
    if (!result) return;
    const { view, content } = result;

    const currentMode = SettingsHelper.getSetting('gameMode', 'Blitz');

    // モードデータ
    const modes = [
      {
        id: 'Blitz',
        iconPath: 'm320-80 40-280H160l360-520h80l-40 320h240L400-80h-80Z',
        name: 'Blitz',
        desc: '時間制限モード',
      },
      {
        id: 'Classic',
        iconPath:
          'M120-80v-180q0-25 17.5-42.5T180-320h72l24-170H170v-60h620v60H684l24 170h72q25 0 42.5 17.5T840-260v180H120Zm140-530-60-270q31 30 68 46.5t75 16.5q37 0 71.5-16t65.5-47q31 31 65.5 47t71.5 16q37 0 74.5-16.5T760-880l-60 270H260Z',
        name: 'Classic',
        desc: '手数制限モード',
      },
    ];

    // グリッドコンテナ作成
    const grid = document.createElement('div');
    grid.className = 'settings-toggle settings-toggle--mode';

    // モードカード作成とハンドラーアタッチ
    modes.forEach((mode) => {
      const card = this.createModeCard(mode, currentMode === mode.id);
      this.attachModeHandler(card, mode, grid, ctx);
      grid.appendChild(card);
    });

    view.appendChild(grid);
    content.appendChild(view);
  },
};

const SkillRenderer = {
  render(ctx) {
    const result = DOMHelper.initializeSettingsView();
    if (!result) return;
    const { view, content } = result;

    const currentSkill = SettingsHelper.getSetting('selectedSkill', 'area5x5');

    // グリッドコンテナ作成
    const grid = document.createElement('div');
    grid.className = 'skill-grid';

    // スキル定義から動的に生成
    const skillTypes = getAllSkillTypes();

    skillTypes.forEach((skillType) => {
      const def = getSkillDefinition(skillType);
      if (!def) {
        MinimalErrorTracker.warn('UI', `Skill definition not found: ${skillType}`);
        return;
      }

      const card = document.createElement('button');
      card.className = SKILL_CARD_CLASS;
      card.type = 'button';
      if (currentSkill === skillType) {
        card.classList.add('is-active');
      }

      // アイコンをヘルパーで生成
      const icon = createSkillIcon(def.ui.icon, SKILL_CARD_ICON_CLASS);

      const name = document.createElement('div');
      name.className = SKILL_CARD_NAME_CLASS;
      name.textContent = def.ui.name;

      const desc = document.createElement('div');
      desc.className = SKILL_CARD_DESC_CLASS;
      desc.textContent = def.ui.description;

      card.appendChild(icon);
      card.appendChild(name);
      card.appendChild(desc);

      card.addEventListener(
        'click',
        guardAsyncButton(async () => {
          const currentSkillNow = SettingsHelper.getSetting('selectedSkill', 'area5x5');
          if (currentSkillNow === skillType) {
            return;
          }

          await handleCriticalSettingChange(
            () => {
              SettingsHelper.setSetting('selectedSkill', skillType);
              // 選択状態を更新
              grid
                .querySelectorAll(`.${SKILL_CARD_CLASS}`)
                .forEach((c) => c.classList.remove('is-active'));
              card.classList.add('is-active');
            },
            ctx?.closeSettings,
            '今のプレイがリセットされます。\nスキルを変更しますか?',
            ctx?.menuSystem,
            { closeOnComplete: false },
          );
        }),
      );

      grid.appendChild(card);
    });

    view.appendChild(grid);
    content.appendChild(view);
  },
};

const SettingsViewRenderer = {
  createRetryCard({ closeSettings, menuSystem }) {
    const card = createActionCard('Retry');

    card.addEventListener(
      'click',
      guardAsyncButton(async (ev) => {
        ev.stopPropagation();

        const effectiveStatus = getEffectiveCoreStatus();

        // Title由来であれば確認ダイアログ不要
        if (effectiveStatus === GameStatus.Title) {
          closeSettingsView(closeSettings, menuSystem, { force: true });
          return;
        }

        const confirmed = await showConfirmation({
          message: '今のプレイをやり直します。\nよろしいですか？',
          confirmLabel: 'Retry',
          menuSystem,
        });

        if (!confirmed) {
          return;
        }

        // 進行中セッションをリセット
        try {
          await resetToTitle();
          closeSettingsView(closeSettings, menuSystem, { force: true });
        } catch (error) {
          MinimalErrorTracker.warn('UI', 'Retry failed', error);
        }
      }),
    );

    return card;
  },

  createTutorialCard({ closeSettings, menuSystem }) {
    const card = createActionCard('Tutorial');

    card.addEventListener(
      'click',
      guardAsyncButton(async (ev) => {
        ev.stopPropagation();

        const effectiveStatus = getEffectiveCoreStatus();

        if (effectiveStatus !== GameStatus.Title) {
          const confirmed = await showConfirmation({
            message: '今のプレイがリセットされます。\nチュートリアルを開始しますか？',
            confirmLabel: 'Restart',
            menuSystem,
          });

          if (!confirmed) {
            return;
          }
        }

        try {
          if (effectiveStatus !== GameStatus.Title) {
            await resetToTitle();
          }

          resetTutorialProgress();
          closeSettingsView(closeSettings, menuSystem, { force: true });
        } catch (error) {
          MinimalErrorTracker.warn('UI', 'Tutorial reset failed', error);
        }
      }),
    );

    return card;
  },

  createClearCard({ closeSettings, menuSystem }) {
    const card = createActionCard('Clear data');

    card.addEventListener(
      'click',
      guardAsyncButton(async (ev) => {
        ev.stopPropagation();

        const confirmed = await showConfirmation({
          message: 'プレイ記録を消去します。\nこの操作は取り消せません。',
          confirmLabel: 'Clear data',
          menuSystem,
        });

        if (!confirmed) {
          return;
        }

        try {
          const effectiveStatus = getEffectiveCoreStatus();
          if (effectiveStatus !== GameStatus.Title) {
            await resetToTitle();
          }

          await clearAllScores();
        } catch (error) {
          MinimalErrorTracker.warn('UI', 'Score clear failed', error);
        }

        // 統計リセット（GameState経由）
        gameState.setState(STATE_PATHS.STATS_PLAY_COUNT, UI_CONSTANTS.PLAY_COUNT_INITIAL);
        gameState.setState(STATE_PATHS.STATS_BEST_SCORE, 0);
        gameState.setState(STATE_PATHS.STATS_LONGEST_CHAIN, 0);
        gameState.setState(STATE_PATHS.STATS_BEST_MATCH, 0);

        closeSettingsView(closeSettings, menuSystem, { force: true });
      }),
    );

    return card;
  },

  render(ctx = {}) {
    const { closeSettings, menuSystem } = ctx;
    const result = DOMHelper.initializeSettingsView();
    if (!result) return;
    const { view, content } = result;

    // アクションカードグリッド
    const grid = document.createElement('div');
    grid.className = SETTINGS_ACTION_LIST_CLASS;

    // カード作成と追加
    const retryCard = this.createRetryCard({ closeSettings, menuSystem });
    const clearCard = this.createClearCard({ closeSettings, menuSystem });
    const tutorialCard = this.createTutorialCard({ closeSettings, menuSystem });

    grid.appendChild(retryCard);
    grid.appendChild(clearCard);
    grid.appendChild(tutorialCard);

    view.appendChild(grid);
    content.appendChild(view);
  },
};

const ConfirmRenderer = {
  render(ctx = {}) {
    const payload = ctx.payload || {};
    const { message = '', confirmLabel = 'OK' } = payload;

    const result = DOMHelper.initializeSettingsView(
      'settings-view settings-view--confirm u-transform-center-x',
    );
    if (!result) return;
    const { view, content } = result;

    if (message) {
      const messageEl = document.createElement('p');
      messageEl.className = 'confirm-view__message';
      messageEl.textContent = message;
      view.appendChild(messageEl);
    }

    const actions = document.createElement('div');
    actions.className = 'confirm-view__actions';

    const confirmBtn = document.createElement('button');
    confirmBtn.type = 'button';
    confirmBtn.className = 'btn-base btn-rectangular confirm-view__button';
    confirmBtn.textContent = confirmLabel || 'OK';
    confirmBtn.addEventListener('click', () => {
      ctx.menuSystem?.handleConfirmResult(true);
    });

    actions.appendChild(confirmBtn);

    view.appendChild(actions);
    content.appendChild(view);

    requestAnimationFrame(() => {
      confirmBtn.focus();
    });
  },
};

export const SettingsRenderers = {
  records: { title: 'Records', render: RecordsRenderer.render.bind(RecordsRenderer) },
  mode: { title: 'Game Mode', render: StyleRenderer.render.bind(StyleRenderer) },
  skill: { title: 'Skill', render: SkillRenderer.render.bind(SkillRenderer) },
  settings: {
    title: 'Settings',
    render: SettingsViewRenderer.render.bind(SettingsViewRenderer),
  },
  confirm: { title: 'Confirm', render: ConfirmRenderer.render.bind(ConfirmRenderer) },
};

export function renderSettingsView(rendererName) {
  const renderer = SettingsRenderers[rendererName];
  if (renderer && typeof renderer.render === 'function') {
    renderer.render();
  } else {
    MinimalErrorTracker.warn(`Settings renderer '${rendererName}' not found`);
  }
}

export { DOMHelper };
